import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { Timeline } from 'primereact/timeline';
import { InputText } from 'primereact/inputtext';
import axios from '../config/axios.config';

const PlanCustomization = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [customizationDialog, setCustomizationDialog] = useState(false);
    const [historyDialog, setHistoryDialog] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [customization, setCustomization] = useState({
        monthly_price: 0,
        yearly_price: 0,
        description: '',
        change_reason: ''
    });
    const [errors, setErrors] = useState({});
    const toast = useRef(null);

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/admin/plans');
            if (response.data.success) {
                setPlans(response.data.plans);
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch plans',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const openCustomizationDialog = (plan) => {
        setSelectedPlan(plan);
        setCustomization({
            monthly_price: plan.current_monthly_price,
            yearly_price: plan.current_yearly_price,
            description: plan.current_description || plan.description,
            change_reason: ''
        });
        setErrors({});
        setCustomizationDialog(true);
    };

    const closeCustomizationDialog = () => {
        setCustomizationDialog(false);
        setSelectedPlan(null);
        setCustomization({
            monthly_price: 0,
            yearly_price: 0,
            description: '',
            change_reason: ''
        });
        setErrors({});
    };

    const saveCustomization = async () => {
        if (!selectedPlan) return;

        // Basic validation
        const newErrors = {};
        if (!customization.monthly_price || customization.monthly_price <= 0) {
            newErrors.monthly_price = 'Monthly price is required';
        }
        if (!customization.yearly_price || customization.yearly_price <= 0) {
            newErrors.yearly_price = 'Yearly price is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            const response = await axios.post(`/admin/plans/${selectedPlan.id}/customize`, customization);
            
            if (response.data.success) {
                const razorpayInfo = response.data.data?.razorpay_plan_id_monthly 
                    ? ' (Razorpay plans created successfully)'
                    : '';
                
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.data.message + razorpayInfo,
                    life: 5000
                });
                closeCustomizationDialog();
                fetchPlans();
            }
        } catch (error) {
            if (error.response?.data?.message) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.response.data.message,
                    life: 5000
                });
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to save customization',
                    life: 3000
                });
            }
        }
    };

    const deleteCustomization = (customizationId) => {
        confirmDialog({
            message: 'Are you sure you want to revert to base plan pricing?',
            header: 'Confirm',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    const response = await axios.delete(`/admin/plans/customization/${customizationId}`);
                    if (response.data.success) {
                        toast.current.show({
                            severity: 'success',
                            summary: 'Success',
                            detail: response.data.message,
                            life: 3000
                        });
                        fetchPlans();
                    }
                } catch (error) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to delete customization',
                        life: 3000
                    });
                }
            }
        });
    };

    // Column Templates
    const planNameTemplate = (rowData) => {
        return (
            <div>
                <div className="font-bold">{rowData.name}</div>
                <div className="text-sm text-gray-500">{rowData.current_description}</div>
            </div>
        );
    };

    const priceTemplate = (rowData) => {
        const hasCustomization = rowData.has_customization;
        const monthlyDiff = hasCustomization ? 
            ((rowData.current_monthly_price - rowData.base_monthly_price) / rowData.base_monthly_price * 100).toFixed(1) : 0;
        
        return (
            <div>
                <div className="flex align-items-center gap-2">
                    <span className="font-semibold">₹{rowData.current_monthly_price}/mo</span>
                    {hasCustomization && monthlyDiff != 0 && (
                        <Tag 
                            value={`${monthlyDiff > 0 ? '+' : ''}${monthlyDiff}%`}
                            severity={monthlyDiff > 0 ? 'warning' : 'success'}
                            className="text-xs"
                        />
                    )}
                </div>
                <div className="text-sm text-gray-500">
                    ₹{rowData.current_yearly_price}/yr
                </div>
            </div>
        );
    };

    const boundariesTemplate = (rowData) => {
        return (
            <div className="text-sm">
                <div>Monthly: ₹{rowData.min_monthly_price} - ₹{rowData.max_monthly_price}</div>
                <div>Yearly: ₹{rowData.min_yearly_price} - ₹{rowData.max_yearly_price}</div>
            </div>
        );
    };

    const featuresTemplate = (rowData) => {
        return (
            <div className="flex flex-column gap-1">
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-database text-sm"></i>
                    <span className="text-sm">{rowData.api_credits} API Credits</span>
                </div>
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-cloud text-sm"></i>
                    <span className="text-sm">{rowData.s3_storage} GB Storage</span>
                </div>
            </div>
        );
    };

    const statusTemplate = (rowData) => {
        if (!rowData.has_customization) {
            return <Badge value="Using Base" severity="info" />;
        }
        
        return <Badge value="Customized" severity="success" />;
    };

    const openHistoryDialog = async (plan) => {
        setSelectedPlan(plan);
        setHistoryDialog(true);
        setHistoryLoading(true);
        
        try {
            const response = await axios.get(`/admin/plans/${plan.id}/history`);
            if (response.data.success) {
                setHistory(response.data.history);
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch history',
                life: 3000
            });
        } finally {
            setHistoryLoading(false);
        }
    };

    const reactivateVersion = async (version) => {
        confirmDialog({
            message: `Reactivate version ${version.version} with price ₹${version.monthly_price}/mo?`,
            header: 'Confirm Reactivation',
            icon: 'pi pi-refresh',
            accept: async () => {
                const reason = prompt('Please provide a reason for reactivating this version:');
                if (!reason) return;
                
                try {
                    const response = await axios.post(`/admin/plans/customization/${version.id}/reactivate`, {
                        change_reason: reason
                    });
                    
                    if (response.data.success) {
                        toast.current.show({
                            severity: 'success',
                            summary: 'Success',
                            detail: response.data.message,
                            life: 3000
                        });
                        setHistoryDialog(false);
                        fetchPlans();
                    }
                } catch (error) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.response?.data?.message || 'Failed to reactivate version',
                        life: 3000
                    });
                }
            }
        });
    };

    const actionsTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text"
                    tooltip="Edit Pricing"
                    onClick={() => openCustomizationDialog(rowData)}
                />
                {rowData.has_customization && (
                    <>
                        <Button
                            icon="pi pi-history"
                            className="p-button-rounded p-button-text p-button-info"
                            tooltip="View History"
                            onClick={() => openHistoryDialog(rowData)}
                        />
                        <Button
                            icon="pi pi-undo"
                            className="p-button-rounded p-button-text p-button-warning"
                            tooltip="Revert to Base"
                            onClick={() => deleteCustomization(rowData.customization_id)}
                        />
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="grid">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="col-12">
                <h2>Plan Pricing Customization</h2>
                <p className="text-gray-600">
                    Customize plan prices within the boundaries set by central admin. 
                    Each price change creates a new version and syncs with Razorpay automatically.
                </p>
            </div>

            {/* Plans Table */}
            <div className="col-12">
                <Card>
                    <DataTable 
                        value={plans} 
                        loading={loading}
                        responsiveLayout="scroll"
                        className="p-datatable-sm"
                    >
                        <Column field="name" header="Plan" body={planNameTemplate} />
                        <Column field="current_monthly_price" header="Current Pricing" body={priceTemplate} />
                        <Column header="Price Boundaries" body={boundariesTemplate} />
                        <Column field="features" header="Features (Fixed)" body={featuresTemplate} />
                        <Column field="status" header="Status" body={statusTemplate} />
                        <Column body={actionsTemplate} header="Actions" style={{ width: '120px' }} />
                    </DataTable>
                </Card>
            </div>

            {/* Customization Dialog */}
            <Dialog
                visible={customizationDialog}
                style={{ width: '500px' }}
                header={`Customize: ${selectedPlan?.name}`}
                modal
                onHide={closeCustomizationDialog}
            >
                {selectedPlan && (
                    <div>
                        {/* Info Message */}
                        <Message 
                            severity="info" 
                            text="Edit prices within boundaries. Changes will be synced with Razorpay automatically."
                            className="mb-3 w-full"
                        />

                        {/* Monthly Price */}
                        <div className="field">
                            <label htmlFor="monthly_price">
                                Monthly Price (₹{selectedPlan.min_monthly_price} - ₹{selectedPlan.max_monthly_price})
                            </label>
                            <div className="p-inputgroup">
                                <span className="p-inputgroup-addon">₹</span>
                                <InputNumber
                                    id="monthly_price"
                                    value={customization.monthly_price}
                                    onValueChange={(e) => {
                                        setCustomization({ ...customization, monthly_price: e.value });
                                        setErrors({ ...errors, monthly_price: null });
                                    }}
                                    min={selectedPlan.min_monthly_price}
                                    max={selectedPlan.max_monthly_price}
                                    showButtons
                                    className={errors.monthly_price ? 'p-invalid' : ''}
                                />
                            </div>
                            {errors.monthly_price && (
                                <small className="p-error">{errors.monthly_price}</small>
                            )}
                            <small className="text-gray-500 block mt-1">
                                Base price: ₹{selectedPlan.base_monthly_price}
                            </small>
                        </div>

                        {/* Yearly Price */}
                        <div className="field">
                            <label htmlFor="yearly_price">
                                Yearly Price (₹{selectedPlan.min_yearly_price} - ₹{selectedPlan.max_yearly_price})
                            </label>
                            <div className="p-inputgroup">
                                <span className="p-inputgroup-addon">₹</span>
                                <InputNumber
                                    id="yearly_price"
                                    value={customization.yearly_price}
                                    onValueChange={(e) => {
                                        setCustomization({ ...customization, yearly_price: e.value });
                                        setErrors({ ...errors, yearly_price: null });
                                    }}
                                    min={selectedPlan.min_yearly_price}
                                    max={selectedPlan.max_yearly_price}
                                    showButtons
                                    className={errors.yearly_price ? 'p-invalid' : ''}
                                />
                            </div>
                            {errors.yearly_price && (
                                <small className="p-error">{errors.yearly_price}</small>
                            )}
                            <small className="text-gray-500 block mt-1">
                                Base price: ₹{selectedPlan.base_yearly_price}
                            </small>
                        </div>

                        {/* Description */}
                        <div className="field">
                            <label htmlFor="description">Description (Optional)</label>
                            <InputTextarea
                                id="description"
                                value={customization.description}
                                onChange={(e) => setCustomization({ ...customization, description: e.target.value })}
                                rows={3}
                                className="w-full"
                                placeholder="Customize plan description..."
                            />
                        </div>

                        {/* Change Reason */}
                        <div className="field">
                            <label htmlFor="change_reason">Change Reason (Optional)</label>
                            <InputText
                                id="change_reason"
                                value={customization.change_reason}
                                onChange={(e) => setCustomization({ ...customization, change_reason: e.target.value })}
                                className="w-full"
                                placeholder="Reason for price change..."
                            />
                        </div>

                        <Divider />

                        {/* Features Info */}
                        <div className="mb-3">
                            <h5 className="mb-2">Fixed Features (Cannot be changed)</h5>
                            <div className="flex gap-3">
                                <Tag icon="pi pi-database" value={`${selectedPlan.api_credits} API Credits`} />
                                <Tag icon="pi pi-cloud" value={`${selectedPlan.s3_storage} GB Storage`} />
                            </div>
                        </div>

                        <div className="flex justify-content-end gap-2">
                            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={closeCustomizationDialog} />
                            <Button label="Save Customization" icon="pi pi-check" onClick={saveCustomization} />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* History Dialog */}
            <Dialog
                visible={historyDialog}
                style={{ width: '700px' }}
                header={`Version History: ${selectedPlan?.name}`}
                modal
                onHide={() => setHistoryDialog(false)}
            >
                {historyLoading ? (
                    <div className="text-center p-5">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                    </div>
                ) : (
                    <div>
                        {history.length === 0 ? (
                            <Message severity="info" text="No version history available" className="w-full" />
                        ) : (
                            <DataTable value={history} responsiveLayout="scroll" className="p-datatable-sm">
                                <Column 
                                    field="version" 
                                    header="Version" 
                                    body={(rowData) => (
                                        <div className="flex align-items-center gap-2">
                                            <span>v{rowData.version}</span>
                                            {rowData.is_current && <Badge value="Current" severity="success" />}
                                        </div>
                                    )}
                                />
                                <Column 
                                    field="monthly_price" 
                                    header="Monthly Price" 
                                    body={(rowData) => `₹${rowData.monthly_price}`}
                                />
                                <Column 
                                    field="yearly_price" 
                                    header="Yearly Price" 
                                    body={(rowData) => `₹${rowData.yearly_price}`}
                                />
                                <Column 
                                    field="subscriber_count" 
                                    header="Subscribers" 
                                    body={(rowData) => (
                                        <Badge value={rowData.subscriber_count} severity={rowData.subscriber_count > 0 ? 'success' : 'info'} />
                                    )}
                                />
                                <Column 
                                    field="created_by" 
                                    header="Changed By" 
                                />
                                <Column 
                                    field="change_reason" 
                                    header="Reason" 
                                    style={{ maxWidth: '200px' }}
                                />
                                <Column 
                                    field="effective_from" 
                                    header="Effective Date" 
                                    body={(rowData) => new Date(rowData.effective_from).toLocaleDateString()}
                                />
                                <Column 
                                    body={(rowData) => (
                                        <div className="flex gap-2">
                                            {!rowData.is_current && rowData.subscriber_count === 0 && (
                                                <Button
                                                    icon="pi pi-refresh"
                                                    className="p-button-rounded p-button-text p-button-success"
                                                    tooltip="Reactivate"
                                                    onClick={() => reactivateVersion(rowData)}
                                                />
                                            )}
                                        </div>
                                    )}
                                    style={{ width: '80px' }}
                                />
                            </DataTable>
                        )}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default PlanCustomization;